import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import LendingModal from './LendingModal';

const LendingTracker = ({ appUser, onBack, onShowToast }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [friends, setFriends] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('lending_items')
        .select('*')
        .eq('user_id', appUser.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('Error fetching lending items:', error);
      if (onShowToast) onShowToast('ไม่สามารถโหลดข้อมูลได้', 'error');
    } finally {
      setLoading(false);
    }
  }, [appUser, onShowToast]);

  const fetchFriends = useCallback(async () => {
    if (!appUser) return;
    try {
      const { data, error } = await supabase
        .from('friendships')
        .select(`
          user_id_1,
          user_id_2,
          user1:users!user_id_1(id, display_name, picture_url, username),
          user2:users!user_id_2(id, display_name, picture_url, username)
        `)
        .or(`user_id_1.eq.${appUser.id},user_id_2.eq.${appUser.id}`)
        .eq('status', 'accepted');

      if (error) throw error;
      const friendList = (data || []).map(f => (f.user_id_1 === appUser.id ? f.user2 : f.user1));
      setFriends(friendList);
    } catch (error) {
      console.error('Error fetching friends:', error.message);
    }
  }, [appUser]);

  useEffect(() => {
    if (appUser) {
      fetchItems();
      fetchFriends();
    }
  }, [appUser, fetchItems, fetchFriends]);

  const handleSave = async ({ itemName, personName, type, lentOn }) => {
    setLoading(true);
    try {
      const { error } = await supabase.from('lending_items').insert([{
        user_id: appUser.id,
        item_name: itemName.trim(),
        person_name: personName.trim(),
        type,
        lent_on: lentOn,
        status: 'active'
      }]);

      if (error) throw error;
      if (onShowToast) onShowToast('บันทึกรายการเรียบร้อย');
      setIsModalOpen(false);
      fetchItems();
    } catch (error) {
      console.error('Error saving item:', error);
      if (onShowToast) onShowToast('เกิดข้อผิดพลาดในการบันทึก', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
      setDeleteId(id);
      setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
        const { error } = await supabase.from('lending_items').delete().eq('id', deleteId);
        if (error) throw error;
        if (onShowToast) onShowToast('ลบรายการแล้ว');
        fetchItems();
    } catch (error) {
        console.error('Error deleting item:', error);
        if (onShowToast) onShowToast('ลบไม่สำเร็จ', 'error');
    } finally {
        setIsDeleteModalOpen(false);
        setDeleteId(null);
    }
  };

  
  const toggleStatus = async (item) => {
    const newStatus = item.status === 'active' ? 'returned' : 'active';
    try {
        const { error } = await supabase
            .from('lending_items')
            .update({ status: newStatus })
            .eq('id', item.id);
        if (error) throw error;
        if (onShowToast) onShowToast('อัปเดตสถานะแล้ว');
        fetchItems();
    } catch (error) {
        if (onShowToast) onShowToast('ไม่สามารถอัปเดตสถานะได้', 'error');
    }
  };

  return (
    <div style={{ padding: '20px', paddingBottom: '100px', backgroundColor: 'var(--color-bg)', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <button 
            onClick={onBack}
            style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', marginBottom: '15px' }}
        >
            &larr; กลับหน้าหลัก
        </button>
        <h2 style={{ color: 'var(--color-primary)', textAlign: 'center', marginBottom: '20px' }}>บันทึกยืม-คืนของ</h2>

        {loading && items.length === 0 ? <p style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>กำลังโหลด...</p> : items.length === 0 ? <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-subtle)', backgroundColor: '#fff', borderRadius: '16px', border: '1px solid var(--color-border)' }}>ยังไม่มีรายการยืม-คืน</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {items.map(item => (
                    <div key={item.id} style={{ backgroundColor: item.status === 'returned' ? 'var(--color-surface-muted)' : '#fff', padding: '16px', borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', opacity: item.status === 'returned' ? 0.7 : 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                    <p style={{ margin: 0, fontSize: '15px', color: 'var(--color-text)', fontWeight: '600' }}>{item.person_name}</p>
                                    <span style={{ 
                                        fontSize: '10px', 
                                        padding: '2px 8px', 
                                        borderRadius: '10px', 
                                        backgroundColor: item.type === 'lent' ? 'var(--color-primary-soft)' : '#E3F2FD',
                                        color: item.type === 'lent' ? 'var(--color-primary)' : '#1565C0',
                                        fontWeight: 'bold'
                                    }}>
                                        {item.type === 'lent' ? 'ให้ยืม' : 'ยืม'}
                                    </span>
                                </div>
                                <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-muted)', textDecoration: item.status === 'returned' ? 'line-through' : 'none' }}>
                                    {item.item_name}
                                </p>
                                <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--color-text-subtle)' }}>{new Date(item.lent_on).toLocaleDateString('th-TH')}</p>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                                <button onClick={() => toggleStatus(item)} style={{ padding: '6px 12px', borderRadius: '20px', border: '1px solid var(--color-border)', backgroundColor: item.status === 'active' ? '#fff' : '#E0E0E0', color: item.status === 'active' ? 'var(--color-primary)' : '#757575', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', minWidth: '80px' }}>
                                    {item.status === 'active' ? 'คืนแล้ว' : 'ยังไม่คืน'}
                                </button>
                                <button onClick={() => handleDeleteClick(item.id)} style={{ background: 'none', border: 'none', color: '#ff5252', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>
                                    ลบ
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        )}
      </div>

      <div style={{
        position: 'fixed',
        bottom: '30px',
        left: '0',
        right: '0',
        display: 'flex',
        justifyContent: 'center',
        zIndex: 100,
        pointerEvents: 'none'
      }}>
        <button
          onClick={() => setIsModalOpen(true)}
          style={{
            pointerEvents: 'auto',
            width: '90%',
            maxWidth: '400px',
            padding: '16px',
            backgroundColor: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)',
            color: 'white',
            border: 'none',
            borderRadius: '50px',
            fontSize: '18px',
            fontWeight: 'bold',
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            cursor: 'pointer'
          }}
        >
          + เพิ่มรายการใหม่
        </button>
      </div>

      <LendingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        loading={loading}
        friends={friends}
      />

      {isDeleteModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2200 }}>
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '16px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-text)' }}>ยืนยันการลบ</h3>
                <p style={{ color: 'var(--color-text-muted)', marginBottom: '25px' }}>คุณต้องการลบรายการนี้ใช่หรือไม่?</p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                    <button 
                        onClick={() => setIsDeleteModalOpen(false)}
                        style={{ padding: '10px 20px', backgroundColor: '#fff', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                        ยกเลิก
                    </button>
                    <button 
                        onClick={confirmDelete}
                        style={{ padding: '10px 20px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 6px rgba(244, 67, 54, 0.3)' }}
                    >
                        ลบรายการ
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default LendingTracker;
